import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import usePageLoaded from '../../hooks/usePageLoaded'
import { useInitOfSavingPath, useSaveAvatar } from '../../hooks/useBackButton'

import ProfileInfo from '../../components/profile/profileInfo'
import AlbumPreview from '../../components/profile/materials/albumPreview'
import ContributionsPreview from '../../components/profile/materials/contributionsPreview'
import GuestbookPreview from '../../components/profile/materials/guestbookPreview'
import GiftsPreview from '../../components/profile/materials/giftsPreview'

import { Helmet } from 'react-helmet'

import { RootState } from '../../store/rootReducer'
import { ProfileState } from '../../store/profile/profileTypings'
import { AlbumType, PhotoType } from '../../store/commonTypes'

import { fetchContributionsInProfile, fetchProfile, fetchProfileGifts } from '../../store/profile/profileSlice'
import { genders, pages } from '../../constants'
import { checkAuth } from '../../utils/auth'
import './profile.sass'
import ForumBlock from '../../components/forumBlock/ForumBlock'
import { setProfileVisited } from '../../api/profile'
import NotFound from '../notFound/notFound'
import { META } from '../../constants/meta'
import { getLangFromPath } from '../../i18next'
import { addLocationToString } from '../../utils/meta'

const Profile = () => {
    usePageLoaded()
    useInitOfSavingPath()
    useSaveAvatar()

    const [t] = useTranslation()
    const location = useLocation()

    const {
        user,
        userPhotos,
        userContributions,
        userGuestbook,
        contributionsTotal,
        userGifts,
        guestbookTotal,
        userForumPosts,
        userForumPostsTotal
    } = useSelector<RootState, ProfileState>((state) => state.profile)
    const loggedIn = useSelector((state: RootState) => state.member.loggedIn)

    const buildTitle = () => {
        let title = ''
        if (user) {
            title += user.nick
            if (user.gender) title += `, ${t(genders.find((x) => x.value === user.gender).label)}`
            if (user.region) title += `, ${user.region}`
            if (user.country) title += `, ${user.country}`
        }
        return title
    }

    const [title, setTitle] = useState(user ? buildTitle() : 'profile')

    const publicAlbum: AlbumType = user
        ? {
              id: 'public',
              title: 'Public album',
              list: [...userPhotos.filter((photo: PhotoType) => photo.type === 'free')]
          }
        : null

    useEffect(() => {
        if (user) {
            const twoWeeksIso = 1000 * 60 * 60 * 24 * 7 * 2
            const visitedProfiles = JSON.parse(localStorage.getItem('visitedProfiles')) || {}
            if (
                (loggedIn && visitedProfiles && !visitedProfiles[user.id]) ||
                (loggedIn && visitedProfiles && Number(new Date()) - visitedProfiles[user.id] > twoWeeksIso)
            ) {
                const newObj = {}
                newObj[`${user.id}`] = Number(new Date())
                localStorage.setItem('visitedProfiles', JSON.stringify(Object.assign(visitedProfiles, newObj)))
                setProfileVisited({ user_id: user.id }).catch((e) => console.log(e))
            }
        }
    }, [])

    return (
        <div className="pageContent" style={{ margin: '0 auto', maxWidth: '1360px', padding: '90px 27px' }}>
            <Helmet>
                <title>
                    {user &&
                        addLocationToString(
                            META.profile[getLangFromPath(location.pathname)].title
                                .replace('{NICK}', user.nick)
                                .replace('{GENDER}', t(genders.find((x) => x.value === user.gender).label)),
                            user
                        )}
                </title>
                <meta
                    name="description"
                    content={
                        user && user.about
                            ? user.about
                            : user
                            ? addLocationToString(
                                  META.profile[getLangFromPath(location.pathname)].description
                                      .replace('{NICK}', user.nick)
                                      .replace('{GENDER}', t(genders.find((x) => x.value === user.gender).label)),
                                  user
                              )
                            : ''
                    }
                />
                <link
                    rel="canonical"
                    href={process.env.CLIENT_PUBLIC_PATH.slice(0, -1) + location.pathname + location.search}
                />
            </Helmet>
            {user ? (
                <>
                    <ProfileInfo user={user} />
                    <AlbumPreview user={user} album={publicAlbum} />
                    <ContributionsPreview
                        user={user}
                        contributions={userContributions}
                        contributionsTotal={contributionsTotal}
                        direction={pages.profile}
                    />
                    {/*@ts-ignore*/}
                    <GiftsPreview recipient={user.nick} gifts={userGifts} isOutgoing={false} />
                    {userForumPosts.length ? (
                        <ForumBlock
                            forumPosts={userForumPosts}
                            userNick={user.nick}
                            userForumPostsTotal={userForumPostsTotal}
                        />
                    ) : (
                        <></>
                    )}
                    <GuestbookPreview user={user} guestbookItems={userGuestbook} guestbookTotal={guestbookTotal} />
                </>
            ) : (
                <NotFound />
            )}
        </div>
    )
}

// @ts-ignore
Profile.getInitialProps = async ({ store, match }) => {
    await checkAuth(store)
    const {
        member: { loggedIn }
    } = store.getState()
    const {
        params: { nick }
    } = match
    // if (loggedIn) {
    //     await loadMember(store)
    // }
    // prev action should be changed when Aleksei finish API for follower checking
    await store.dispatch(fetchProfile({ nick }))
    const {
        profile: { user }
    } = store.getState()
    if (user && user.id) {
        await store.dispatch(fetchContributionsInProfile({ user_id: user.id, p: 1 }))
        await store.dispatch(fetchProfileGifts({ user_id: user.id }))
    } else {
        return { statusCode: 404 }
    }
}

export default Profile
